import { FormEvent, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { LoginForgotForm } from "./LoginForgotForm";
import { LoginMfaSetup } from "./LoginMfaSetup";
import { LoginScene3D } from "./loginScene/LoginScene3D";
import {
  ApiError,
  SessionBootstrapResult,
  SessionPayload,
  apiPost,
  connectionBannerToFormMessage,
  userFacingLoginError,
} from "../../api";
import { firstNavigationHrefFromPayload } from "../../navPaths";

export function LoginScreen({
  csrfToken,
  onLoggedIn,
  bootstrapLoading,
  bootstrapSession,
}: {
  csrfToken?: string;
  onLoggedIn: (session: SessionPayload) => void;
  bootstrapLoading?: boolean;
  bootstrapSession: () => Promise<SessionBootstrapResult>;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState("");
  const [totpRequired, setTotpRequired] = useState(false);
  const [totpSetup, setTotpSetup] = useState(false);
  const [setupTicket, setSetupTicket] = useState("");
  const [setupSecret, setSetupSecret] = useState("");
  const [setupOtpauth, setSetupOtpauth] = useState("");
  const [forgot, setForgot] = useState(false);
  const [panelView, setPanelView] = useState<"login" | "forgot">("login");
  const [panelPhase, setPanelPhase] = useState<"in" | "out">("in");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [remember, setRemember] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sceneReady, setSceneReady] = useState(false);
  const [bootVisible, setBootVisible] = useState(true);
  const [bootFading, setBootFading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const canSubmit = !bootstrapLoading && !submitting;
  const pageReady = !bootstrapLoading && sceneReady;

  const onSceneReadyChange = useCallback((ready: boolean) => {
    setSceneReady(ready);
  }, []);

  useEffect(() => {
    if (!pageReady || !bootVisible || bootFading) return;
    setBootFading(true);
    const t = window.setTimeout(() => setBootVisible(false), 480);
    return () => window.clearTimeout(t);
  }, [pageReady, bootVisible, bootFading]);

  function switchPanel(next: "login" | "forgot") {
    if (next === panelView && panelPhase === "in") return;
    setPanelPhase("out");
    window.setTimeout(() => {
      setForgot(next === "forgot");
      setPanelView(next);
      setPanelPhase("in");
      setError(null);
      if (next === "login") setForgotSent(false);
    }, 220);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      let token = csrfToken?.trim();
      if (!token) {
        const r = await bootstrapSession();
        if (!r.ok) {
          setError(connectionBannerToFormMessage(r.issue));
          return;
        }
        token = r.session.csrfToken?.trim();
      }
      if (!token) {
        setError("Could not obtain a secure sign-in token. Check that the RMS service is running.");
        return;
      }
      const nextQs = new URLSearchParams(location.search).get("next");
      const nextForBackend =
        typeof nextQs === "string" && nextQs.startsWith("/") && !nextQs.startsWith("//") ? nextQs : "/";
      const payload = await apiPost<
        SessionPayload & {
          totp_required?: boolean;
          totp_setup_required?: boolean;
          setup_ticket?: string;
          secret?: string;
          otpauth_uri?: string;
        }
      >(
        "/api/spa/login",
        { username, password, otp, next: nextForBackend, remember, setup_ticket: setupTicket },
        token
      );
      if (payload?.totp_setup_required) {
        setTotpSetup(true);
        setTotpRequired(false);
        setSetupTicket(String(payload.setup_ticket || setupTicket));
        setSetupSecret(String(payload.secret || setupSecret));
        setSetupOtpauth(String(payload.otpauth_uri || setupOtpauth));
        setOtp("");
        setError(null);
        return;
      }
      if (payload?.totp_required) {
        setTotpRequired(true);
        setTotpSetup(false);
        setError("Enter the Authenticator App code.");
        return;
      }
      if (payload?.authenticated !== true) {
        setError("Sign-in did not complete.");
        return;
      }
      onLoggedIn(payload);
      let dest =
        typeof nextQs === "string" && nextQs.startsWith("/") && !nextQs.startsWith("//") ? nextQs : "/";
      const firstAllowed = firstNavigationHrefFromPayload(payload.navigation);
      if (firstAllowed && (nextQs === null || nextQs === "" || nextQs === "/")) {
        dest = firstAllowed;
      }
      navigate(dest, { replace: true });
    } catch (err) {
      const payload =
        err instanceof ApiError && err.payload && typeof err.payload === "object"
          ? (err.payload as {
              totp_required?: boolean;
              totp_setup_required?: boolean;
              setup_ticket?: string;
              secret?: string;
              otpauth_uri?: string;
            })
          : null;
      if (payload?.totp_setup_required && (payload.setup_ticket || payload.secret || payload.otpauth_uri)) {
        setTotpSetup(true);
        setTotpRequired(false);
        setSetupTicket(String(payload.setup_ticket || setupTicket));
        setSetupSecret(String(payload.secret || setupSecret));
        setSetupOtpauth(String(payload.otpauth_uri || setupOtpauth));
        setOtp("");
        setError(null);
        return;
      }
      if (payload?.totp_required) setTotpRequired(true);
      setError(userFacingLoginError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitForgot(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setForgotSent(false);
    try {
      let token = csrfToken?.trim();
      if (!token) {
        const r = await bootstrapSession();
        if (!r.ok) {
          setError(connectionBannerToFormMessage(r.issue));
          return;
        }
        token = r.session.csrfToken?.trim();
      }
      await apiPost("/api/account/forgot-password", { email: forgotEmail }, token);
      setForgotSent(true);
    } catch (err) {
      setError(userFacingLoginError(err));
    } finally {
      setSubmitting(false);
    }
  }

  const bootStatus = bootstrapLoading
    ? "Connecting to RMS…"
    : sceneReady
      ? "Ready"
      : "Loading 3D infrastructure…";

  return (
    <div className="login-page login-page--3d">
      {bootVisible ? (
        <div className={`login-boot-screen${bootFading ? " done" : ""}`} role="status" aria-live="polite">
          <div className="login-boot-orb" aria-hidden>
            <span className="login-boot-ring" />
            <span className="login-boot-core" />
          </div>
          <p className="login-boot-brand">IoTCore</p>
          <p className="login-boot-status">{bootStatus}</p>
          <div className="login-boot-dots" aria-hidden>
            <span />
            <span />
            <span />
          </div>
        </div>
      ) : null}
      <div className="login-app">
        <section className="login-scene" aria-label="Interactive Three.js 3D smart infrastructure scene">
          <LoginScene3D onReadyChange={onSceneReadyChange} />
          <div className="login-scene-overlay" />
          <div className="login-kicker">KLT RMS · CONNECTED INFRASTRUCTURE</div>
          <div className="login-scene-footer">POWER · TELECOM · ENERGY · IOT</div>
          <div className="login-controls-hint">Drag to rotate · Scroll to zoom · Double-click to reset</div>
        </section>
        <aside className="login-panel">
          <div className="login-card-wrap">
            <div className="login-glass">
              <h1 className="login-title">IoTCore</h1>
              <div
                key={panelView}
                className={`login-panel-swap login-panel-swap--${panelPhase}`}
              >
              {forgot ? (
                <LoginForgotForm
                  email={forgotEmail}
                  onEmail={setForgotEmail}
                  submitting={submitting}
                  error={error}
                  sent={forgotSent}
                  onSubmit={submitForgot}
                  onBack={() => switchPanel("login")}
                />
              ) : (
                <form onSubmit={submit} className="login-card" autoComplete="off" aria-busy={!!bootstrapLoading}>
                  {error ? <div className="login-error">{error}</div> : null}
                  {totpSetup ? (
                    <LoginMfaSetup
                      username={username}
                      secret={setupSecret}
                      otpauth={setupOtpauth}
                      otp={otp}
                      onOtp={setOtp}
                    />
                  ) : totpRequired ? (
                    <>
                      <p className="login-step-note">
                        Enter the 6-digit code from Authenticator App for {username}.
                      </p>
                      <label className="login-field-label" htmlFor="otp">
                        Authenticator code
                      </label>
                      <div className="login-input-wrap">
                        <input
                          id="otp"
                          name="otp"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          value={otp}
                          onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                          className="login-input"
                          placeholder="6-digit code"
                          required
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <label className="login-field-label" htmlFor="username">
                        User
                      </label>
                      <div className="login-input-wrap">
                        <input
                          id="username"
                          name="rms_login_uid"
                          value={username}
                          onChange={(event) => setUsername(event.target.value)}
                          className="login-input"
                          placeholder="Username or email"
                          autoComplete="username"
                          required
                        />
                      </div>
                      <label className="login-field-label" htmlFor="password">
                        Password
                      </label>
                      <div className="login-input-wrap">
                        <input
                          id="password"
                          name="rms_login_pw"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(event) => setPassword(event.target.value)}
                          className="login-input login-input--pw"
                          placeholder="Password"
                          autoComplete="current-password"
                          required
                        />
                        <button
                          type="button"
                          className="login-eye"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowPassword((v) => !v)}
                        >
                          {showPassword ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
                        </button>
                      </div>
                      <div className="login-options">
                        <label className="login-remember">
                          <input
                            type="checkbox"
                            checked={remember}
                            onChange={(event) => setRemember(event.target.checked)}
                          />
                          <span>Remember me</span>
                        </label>
                        <button
                          type="button"
                          className="login-forgot-link"
                          onClick={() => switchPanel("forgot")}
                        >
                          Forgot password?
                        </button>
                      </div>
                    </>
                  )}
                  <div className="login-actions">
                    {totpRequired || totpSetup ? (
                      <button
                        type="button"
                        className="login-step-back"
                        onClick={() => {
                          setTotpRequired(false);
                          setTotpSetup(false);
                          setSetupTicket("");
                          setSetupSecret("");
                          setSetupOtpauth("");
                          setOtp("");
                          setError(null);
                        }}
                      >
                        Back
                      </button>
                    ) : null}
                    <button type="submit" disabled={submitting || !canSubmit} className="login-button">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                      {totpSetup ? "Confirm and sign in" : totpRequired ? "Verify code" : "Log In"}
                    </button>
                  </div>
                </form>
              )}
              </div>
            </div>
            <div className="login-panel-footer">
              SMART, INNOVATIVE, CUSTOMER-CENTRIC
              <small>Powered by KLT RMS</small>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export const nativeReactRoutes = new Set([
  "dashboard",
  "csu_data",
  "csu_templates",
  "csu_template_detail",
  "dashboard_mapping",
  "project_management",
  "site_variable_log",
  "download_tasks",
  "customized_report",
  "smtp_settings",
  "report_task_log",
  "site_log_status",
  "modbus_status",
  "snmp_status",
  "control_action_log",
  "site_offline_history",
  "alarms_history",
  "account_activity",
  "webhook_log",
  "webhook_sender",
  "alarm_webhook",
  "redis_status",
  "api_monitor",
  "settings",
  "users",
  "users_new",
  "user_edit",
  "alarm_rules",
  "offline_alarm_rules",
  "alarm_mapping",
  "alarm_type_mapping",
  "alarm_contact",
  "alarms_active",
  "database_setup",
  "database_structure",
  "database_status",
  "database_status_db",
  "modbus_gateways",
  "modbus_at_command",
  "modbus_uart_setting",
  "modbus_templates",
  "modbus_template_detail",
  "sites",
  "modbus_devices",
  "modbus_device",
  "modbus_device_view",
  "modbus_device_view_machine",
  "modbus_device_poll_debug",
  "modbus_device_ui_config",
  "snmp_templates",
  "snmp_template_detail",
  "mqtt_gateways",
  "mqtt_devices",
  "mqtt_templates",
  "mqtt_template_detail",
  "ping_test",
  "snmp_tester",
  "map_view",
  "inventory_site",
  "inventory_config",
  "map_config",
  "analytics",
  "analytics_config",
  "connector",
  "task_status",
]);
